export type AccountReadSide = "BANKING" | "INVEST";
/** Current Account Read response schema. Unknown versions must not be rendered as facts. */
export const ACCOUNT_READ_SCHEMA_VERSION = "account_read.v3" as const;
export type AccountReadSchemaVersion = typeof ACCOUNT_READ_SCHEMA_VERSION;

export type AccountReadMetricPrimitive = string | number | boolean | null;
export type AccountReadMetricValue =
  | AccountReadMetricPrimitive
  | AccountReadMetricValue[]
  | { [key: string]: AccountReadMetricValue };
export type AccountReadMetrics = Record<string, AccountReadMetricValue>;

export type AccountReadDimensionKey =
  | "subscriptions"
  | "idle_cash"
  | "runway"
  | "annual_bills"
  | "sell_rule"
  | "tax_dates"
  | "portfolio_mix"
  /** @deprecated v2 schema renamed to annual_bills; kept for mock fixtures */
  | "big_bills";

export type AccountReadGeneralBlockKey =
  | "coverage"
  | "net_worth"
  | "account_balances"
  | "monthly_cashflow"
  | "spending_categories"
  | "portfolio_value"
  | "trading_cost";

export interface AccountReadBlock {
  key: AccountReadGeneralBlockKey;
  summary: string;
  metrics?: AccountReadMetrics;
}

export interface AccountReadWatchOption {
  key: string;
  label: string;
  sub: string;
  watchKind: "self_running" | "needs_chat";
  active: boolean;
  trackerId?: string;
}

export interface AccountReadDimension {
  key: AccountReadDimensionKey;
  narrative: string;
  metrics: AccountReadMetrics;
  watchOption?: AccountReadWatchOption;
}

export interface AccountReadReport {
  reportId: string;
  /** Response schema version, when supplied by the API. */
  schemaVersion?: string;
  /** Data checkpoint used to produce this report. */
  sourceDataVersion: string;
  side: AccountReadSide;
  headline: string;
  summaryText: string[];
  publishedAt: string;
  blocks: AccountReadBlock[];
  dimensions: AccountReadDimension[];
  activeWatches: AccountReadWatchOption[];
}

export interface AccountReadUnreadSummary {
  reportId: string;
  side: AccountReadSide;
  headline: string;
  /** First sentence of summary_text (converged unread payload) */
  summary?: string;
  publishedAt: string;
}

export interface AccountReadWatchSubmitResult {
  activated: AccountReadWatchOption[];
  needsChat: AccountReadWatchOption[];
  surface: string;
}

export type AccountReadDetailDto = Record<
  string,
  AccountReadMetricValue | undefined
>;

export interface AccountReadFeedbackPayload {
  side: AccountReadSide;
  reasons: string[];
  detail?: string;
}
